import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FF_CLIENT_OLD_VERSION_RELOAD } from '@/consts/featureFlags';
import { posthog } from '@/providers/posthog';
import type { NodeId } from 'web';

export interface AlertData {
  severity?: 'success' | 'error' | 'warning' | 'info';
  text: string;
  icon: React.ReactNode;
  action?: {
    text: string;
    onClick: () => void | Promise<void>;
  } | null;
}

export interface SnackbarData extends AlertData {
  isOpen: boolean;
  duration?: number | null; // null = never auto-hide
  key?: string;
  minWidth?: number | string; // optional min width (e.g., 300 or '300px')
}

export interface DeleteGroupDialogData {
  type: 'deleteGroup';
  allNodeIdsToDelete: NodeId[];
}

export type DialogData = DeleteGroupDialogData;

interface GlobalState {
  snackbarData: SnackbarData;
  updateSnackbarData: (snackbarData: Partial<SnackbarData>) => void;
  updateSnackbarText: (text: string) => void;
  dialogData?: DialogData;
  openDialog: (dialog: DialogData) => void;
  closeDialog: () => void;
  isShortcutsPanelOpen: boolean;
  setIsShortcutsPanelOpen: (isOpen: boolean) => void;
  isShowRefreshAlert: boolean;
  setIsShowRefreshAlert: (isShow: boolean) => void;
  reset: () => void;
}

export const useGlobalStore = create<GlobalState>()(
  devtools(
    (set) => ({
      // Initial state
      snackbarData: { text: '', isOpen: false, icon: null },
      dialogData: undefined,
      // Actions to update the state
      updateSnackbarData: (snackbarData: Partial<SnackbarData>) =>
        set((state) => {
          return {
            snackbarData: {
              ...state.snackbarData,
              ...snackbarData,
              key: snackbarData.key ?? `snackbar-${Date.now()}`,
            },
          };
        }),
      updateSnackbarText: (text: string) => set((state) => ({ snackbarData: { ...state.snackbarData, text } })),
      openDialog: (dialog: DialogData) => set(() => ({ dialogData: dialog })),
      closeDialog: () => set(() => ({ dialogData: undefined })),
      isShortcutsPanelOpen: false,
      setIsShortcutsPanelOpen: (isOpen: boolean) => set(() => ({ isShortcutsPanelOpen: isOpen })),
      isShowRefreshAlert: false,
      setIsShowRefreshAlert: (isShow: boolean) => {
        if (posthog.isFeatureEnabled(FF_CLIENT_OLD_VERSION_RELOAD)) {
          set(() => ({ isShowRefreshAlert: isShow }));
        }
      },
      reset: () =>
        set(() => ({
          snackbarData: { text: '', isOpen: false, icon: null },
          dialogData: undefined,
          isShortcutsPanelOpen: false,
          isShowRefreshAlert: false,
        })),
    }),
    {
      name: 'Global Store',
      enabled: import.meta.env.DEV,
    },
  ),
);
